import { CreditCardIcon, MoneyIcon } from '@phosphor-icons/react'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { useGetSettingsByToken } from '@/api/market-shop/client/get-settings-by-token'
import { DeliveryRadioOption } from '@/components/business/market-shop/delivery-radio-option'
import { Typography } from '@/components/ui/typography'
import { MarketShopMobilePaymentMethod } from '@/models'

import * as S from './checkout-payment-method.styled'

export type CheckoutPaymentMethodProps = Omit<React.HTMLAttributes<HTMLElement>, 'onChange'> & {
  title: string
  value: MarketShopMobilePaymentMethod | null
  onChange?: (value: MarketShopMobilePaymentMethod) => void
  children?: React.ReactNode
  disabled?: boolean
}

export const CheckoutPaymentMethod = ({
  title,
  value,
  disabled = false,
  children,
  onChange,
  ...rest
}: CheckoutPaymentMethodProps) => {
  const { t } = useTranslation(['market-shop'])
  const { data: settings } = useGetSettingsByToken({ enabled: !disabled })

  /**
   * Conditions are not really best to be read, here is a flow:
   *
   * Visibility logic for payment options:
   * - Checkout View (!disabled): Only show options that are currently enabled in the API settings.
   * - Order View / Disabled State (disabled): Show only the previously selected option (from `value`),
   *   ignoring API settings to ensure the user can still see how an existing order was paid.
   */
  const canShowCashOptionByAPI = disabled || (!!settings?.enablePayByCash && !disabled)
  const canShowCashOption = (!disabled || (disabled && value === 'cash')) && canShowCashOptionByAPI

  const canShowCardOptionByAPI = disabled || (!!settings?.enablePayByCard && !disabled)
  const canShowCardOption = (!disabled || (disabled && value === 'card')) && canShowCardOptionByAPI

  return (
    <S.Section {...rest}>
      <Typography as="h2" size="heading-medium" weight="semibold" color="black800">
        {title}
      </Typography>

      <S.OptionsList>
        {canShowCashOption && (
          <li>
            <DeliveryRadioOption
              icon={MoneyIcon}
              title={
                settings?.displayNameForCashPayment ||
                t('market-shop:checkoutPaymentMethod.cashDefaultLabel')
              }
              active={value === 'cash'}
              disabled={disabled}
              onClick={() => onChange?.('cash')}
            />
          </li>
        )}
        {canShowCardOption && (
          <li>
            <DeliveryRadioOption
              icon={CreditCardIcon}
              title={t('market-shop:checkoutPaymentMethod.cardLabel')}
              active={value === 'card'}
              disabled={disabled}
              onClick={() => onChange?.('card')}
            />
          </li>
        )}
      </S.OptionsList>

      {children}
    </S.Section>
  )
}
